/**
 * Global "Insufficient wallet funds" popup, wired directly to Stripe
 * Elements (PaymentElement) so the user can top up in-place without
 * leaving the page. Falls back to Stripe Checkout if Stripe Elements is
 * not configured (`REACT_APP_STRIPE_PK` missing).
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements, PaymentElement, useStripe, useElements,
} from '@stripe/react-stripe-js';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Wallet, ArrowRight, Loader2, CreditCard, Lock, Check } from 'lucide-react';
import { insufficientFundsInfo, readableApiError } from '../utils/apiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STRIPE_PK = process.env.REACT_APP_STRIPE_PK;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token') || ''}` } });
const fmtUSD = (cents) => `$${(((cents || 0) / 100)).toFixed(2)}`;
const TOPUP_PRESETS = [2000, 5000, 10000, 20000];

let _stripePromise;
const stripePromise = () => {
  if (!_stripePromise && STRIPE_PK) _stripePromise = loadStripe(STRIPE_PK);
  return _stripePromise;
};

const Ctx = createContext({ show: () => {} });

export function InsufficientFundsProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);

  const show = useCallback((payload) => {
    setInfo(payload);
    setOpen(true);
  }, []);

  useEffect(() => {
    const handler = (e) => { if (e?.detail) show(e.detail); };
    window.addEventListener('eventism:insufficient-funds', handler);
    return () => window.removeEventListener('eventism:insufficient-funds', handler);
  }, [show]);

  const value = useMemo(() => ({ show }), [show]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="insufficient-funds-dialog">
          <TopupFlow info={info} onClose={() => setOpen(false)} />
        </DialogContent>
      </Dialog>
    </Ctx.Provider>
  );
}

export function useInsufficientFunds() {
  return useContext(Ctx);
}

export function handleInsufficientFunds(err, showFn) {
  const info = insufficientFundsInfo(err);
  if (info && typeof showFn === 'function') {
    showFn(info);
    return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Two-step flow: pick amount → mount Stripe Elements & confirm
// ---------------------------------------------------------------------------

function TopupFlow({ info, onClose }) {
  const need = info?.shortfall_cents || 2000;
  const initial = TOPUP_PRESETS.find((c) => c >= need) || TOPUP_PRESETS[TOPUP_PRESETS.length - 1];
  const [selected, setSelected] = useState(initial);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [creating, setCreating] = useState(false);

  // Reset when reopened with a new shortfall payload
  useEffect(() => {
    setSelected(initial);
    setClientSecret(null);
    setPaymentIntentId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info?.shortfall_cents]);

  const startElements = async () => {
    if (!STRIPE_PK) {
      // No Stripe Elements key configured → fall back to Checkout redirect.
      await startCheckoutFallback(selected);
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(
        `${API}/organizations/me/wallet/topup/payment-intent`,
        { amount_cents: selected },
        auth(),
      );
      setClientSecret(res.data?.client_secret);
      setPaymentIntentId(res.data?.payment_intent_id);
    } catch (err) {
      toast.error(readableApiError(err, 'Could not start top-up'));
    } finally {
      setCreating(false);
    }
  };

  const startCheckoutFallback = async (amount_cents) => {
    setCreating(true);
    try {
      const res = await axios.post(
        `${API}/organizations/me/wallet/topup`,
        { amount_cents },
        auth(),
      );
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Could not start checkout');
        setCreating(false);
      }
    } catch (err) {
      toast.error(readableApiError(err, 'Top-up failed'));
      setCreating(false);
    }
  };

  // ---- Step 1: amount picker ----
  if (!clientSecret) {
    return (
      <>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-amber-300" /> Not enough wallet balance
          </DialogTitle>
          <DialogDescription>
            {info?.required_cents != null && info?.balance_cents != null ? (
              <>You need <span className="font-semibold text-foreground">{fmtUSD(info.required_cents)}</span>{' '}
                {info?.units ? <>({info.units.toLocaleString()} × {info.unit_kind || 'units'}) </> : null}
                but your balance is <span className="font-semibold text-foreground">{fmtUSD(info.balance_cents)}</span>.
                Top up to keep going.</>
            ) : (
              <>Top up your wallet to continue.</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-4 gap-2 mt-2" data-testid="topup-presets">
          {TOPUP_PRESETS.map((cents) => (
            <button
              key={cents}
              type="button"
              onClick={() => setSelected(cents)}
              className={`rounded-md py-3 px-2 font-semibold tabular-nums transition border ${
                selected === cents
                  ? 'border-indigo-400 bg-indigo-500/15 text-indigo-100'
                  : 'border-border bg-card/60 hover:border-indigo-500/40'
              }`}
              data-testid={`insf-topup-${cents}`}
            >
              {fmtUSD(cents)}
            </button>
          ))}
        </div>

        <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
          <Lock className="h-3 w-3" /> Paid securely via Stripe. Credited instantly.
        </p>

        <div className="flex gap-2 mt-3">
          <Button variant="outline" onClick={onClose} data-testid="insf-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={startElements}
            disabled={creating}
            className="flex-1 bg-gradient-to-r from-indigo-500 to-fuchsia-500 hover:from-indigo-400 hover:to-fuchsia-400 text-white font-bold"
            data-testid="insf-topup-btn"
          >
            {creating ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Preparing…</>
            ) : (
              <><CreditCard className="h-4 w-4 mr-2" /> Continue with {fmtUSD(selected)} <ArrowRight className="h-4 w-4 ml-2" /></>
            )}
          </Button>
        </div>
      </>
    );
  }

  // ---- Step 2: Stripe Elements ----
  const options = {
    clientSecret,
    appearance: { theme: 'night', variables: { colorPrimary: '#6366f1' } },
  };
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-indigo-300" /> Pay {fmtUSD(selected)}
        </DialogTitle>
        <DialogDescription>
          Enter your card details — credited the moment Stripe confirms.
        </DialogDescription>
      </DialogHeader>
      <Elements stripe={stripePromise()} options={options}>
        <ElementsCheckout
          amount={selected}
          paymentIntentId={paymentIntentId}
          onBack={() => { setClientSecret(null); setPaymentIntentId(null); }}
          onSuccess={(balance_cents) => {
            toast.success(`Wallet credited ${fmtUSD(selected)} — balance ${fmtUSD(balance_cents)}`);
            onClose();
          }}
        />
      </Elements>
    </>
  );
}

function ElementsCheckout({ amount, paymentIntentId, onBack, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const onPay = async (e) => {
    e?.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setErrMsg('');
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          // We stay on the page — `redirect: if_required` so cards that need
          // 3DS still get a popup, but normal cards confirm in-place.
        },
        redirect: 'if_required',
      });

      if (error) {
        setErrMsg(error.message || 'Payment failed');
        toast.error(error.message || 'Payment failed');
        setSubmitting(false);
        return;
      }
      // Confirm on backend (also covers the case where PaymentIntent
      // succeeded but the webhook hasn't fired yet).
      const res = await axios.post(
        `${API}/organizations/me/wallet/topup/payment-intent/confirm`,
        { payment_intent_id: paymentIntent?.id || paymentIntentId },
        auth(),
      );
      if (res.data?.credited || res.data?.already_credited) {
        onSuccess(res.data?.balance_cents || 0);
      } else {
        toast.info(`Payment is ${res.data?.status || 'processing'} — your balance will update shortly`);
        onBack();
      }
    } catch (err) {
      const m = err?.response?.data?.detail || err?.message || 'Payment failed';
      setErrMsg(typeof m === 'string' ? m : 'Payment failed');
      toast.error(typeof m === 'string' ? m : 'Payment failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onPay} className="space-y-3" data-testid="insf-stripe-elements">
      <PaymentElement
        options={{ layout: 'tabs' }}
      />
      {errMsg && (
        <p className="text-xs text-rose-300" data-testid="insf-stripe-error">{errMsg}</p>
      )}
      <div className="flex gap-2 pt-1">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          disabled={submitting}
          data-testid="insf-back-btn"
        >
          Back
        </Button>
        <Button
          type="submit"
          disabled={submitting || !stripe || !elements}
          className="flex-1 bg-gradient-to-r from-indigo-500 to-fuchsia-500 hover:from-indigo-400 hover:to-fuchsia-400 text-white font-bold"
          data-testid="insf-pay-btn"
        >
          {submitting ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing…</>
          ) : (
            <><Check className="h-4 w-4 mr-2" /> Pay {fmtUSD(amount)}</>
          )}
        </Button>
      </div>
      <p className="text-[10px] text-muted-foreground flex items-center justify-center gap-1">
        <Lock className="h-3 w-3" /> Encrypted by Stripe. We never see your card details.
      </p>
    </form>
  );
}
